import { Decimal } from 'decimal.js'
import type { FormEvent, ReactElement } from 'react'
import { useState } from 'react'
import { BASIS_POINTS_FULL, removeBasisPoints } from '@/domain/money'
import type { EffectiveValuation } from '~/api/client'
import { formatMinor } from '~/api/client'
import { chartCss } from '~/charts/theme'
import { Card, CardHead } from '~/components/ui/Card'
import { SegmentedControl } from '~/components/ui/Controls'
import { StatusPill } from '~/components/ui/Status'
import { describeAge } from '~/state/dates'
import { useEffectiveValuations, useHoldsThroughCompany, usePrivateHoldings } from '~/state/queries'
import type { PrivateHoldingsSettings } from '~/state/private-holdings'
import { CONFIDENCE_NOTE, CONFIDENCE_TONE, formatBasisPoints } from '~/state/private-holdings'

const HAIRCUT_PRESETS_BP = [0, 1_000, 2_500, 5_000]

const SWITCH_CSS = `
.nw-switch { align-items: flex-start; display: flex; gap: 10px; margin-bottom: 10px; }
.nw-switch input { margin-top: 4px; }
.nw-switch span { display: block; }
.nw-switch small { color: var(--nw-muted); display: block; font-size: var(--nw-text-chip); max-width: 46ch; }
`

const CSS = `
.nw-private p { margin: 0 0 var(--nw-space-3); }
.nw-private .nw-lede { color: var(--nw-body-ink); font-size: var(--nw-text-cell); max-width: 70ch; }
.nw-private .nw-card-head { margin-bottom: var(--nw-space-4); }
.nw-private fieldset { border: 0; margin: var(--nw-space-4) 0 0; padding: 0; }
.nw-private legend { color: var(--nw-muted); font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); padding: 0; text-transform: uppercase; }
.nw-private .nw-haircut { align-items: center; display: flex; flex-wrap: wrap; gap: var(--nw-space-2); margin: var(--nw-space-2) 0 var(--nw-space-2); }
.nw-private input[type='number'] { width: 96px; }
.nw-private .nw-fixed { background: var(--nw-hover); border-radius: var(--nw-radius-md); color: var(--nw-body-ink); font-size: var(--nw-text-cell); margin: var(--nw-space-3) 0 0; padding: 10px var(--nw-space-3); }
.nw-valuation { border-top: var(--nw-hairline-width) solid var(--nw-hairline); margin-top: var(--nw-space-4); padding-top: var(--nw-space-3); }
.nw-valuation .nw-marks { display: flex; flex-wrap: wrap; gap: var(--nw-space-1); }
.nw-valuation dl { display: grid; gap: var(--nw-space-1) var(--nw-space-4); grid-template-columns: max-content 1fr; margin: var(--nw-space-2) 0 0; }
.nw-valuation dt { color: var(--nw-muted); font-size: var(--nw-text-meta); font-weight: var(--nw-weight-strong); letter-spacing: var(--nw-track-label); text-transform: uppercase; }
.nw-valuation dd { font-variant-numeric: tabular-nums; margin: 0; }
.nw-valuation .nw-headline {
  font-size: var(--nw-text-figure); font-weight: var(--nw-weight-figure);
  line-height: var(--nw-text-figure-leading);
}
.nw-valuation small { color: var(--nw-body-ink); font-size: var(--nw-text-cell); font-weight: var(--nw-weight-body); }
`

/**
 * Offered only to a household that holds something priced off a round.
 *
 * Every control here moves a figure that is worked out from a privately valued
 * holding, so with none held they move nothing — and the card would be three
 * live switches, a discount, and a sentence about what is often the largest
 * number here, all addressed to somebody whose figures contain no such number.
 * Nothing is reached through it: a valuation is entered on Manual, and the card
 * appears once one is.
 */
export function PrivateHoldingsPanel(): ReactElement | null {
  const { settings, isPending, isSaving, error, save } = usePrivateHoldings()
  const valuations = useEffectiveValuations()
  const holdsCompany = useHoldsThroughCompany(settings)

  if ((valuations.data?.valuations.length ?? 0) === 0) return null

  return (
    <Card className="nw-private">
      <style>{chartCss + SWITCH_CSS + CSS}</style>
      <CardHead
        id="nw-private-heading"
        title="Private holdings"
        lede="Stale between rounds, priced off a share class these holdings are not, and impossible to sell. They are often the largest number here, so they are counted where that is honest and left out where it is not."
      />

      {error && <p className="nw-error">Could not read the switch: {error.message}</p>}

      <Switch
        label="Count it in the headline"
        hint={
          holdsCompany === true
            ? 'Look-through and Spendable. Owning the shares is a fact, so the default is on.'
            : 'Look-through. Owning the shares is a fact, so the default is on.'
        }
        checked={settings.includeInLookThrough}
        disabled={isPending}
        onChange={(includeInLookThrough) => save({ ...settings, includeInLookThrough })}
      />
      <PrivateHoldingsConcentrationSwitch />

      <p className="nw-fixed">
        Liquid and runway never count it. Those figures answer what can be spent, and none of this
        can be — that is a property of the holding, not a setting.
      </p>

      <Haircut settings={settings} disabled={isPending || isSaving} onChange={save} />

      {valuations.data?.valuations.map((valuation) => (
        <PrivateHoldingValuationNote
          key={valuation.id}
          valuation={valuation}
          haircutBp={settings.haircutBp}
        />
      ))}
    </Card>
  )
}

/**
 * The same switch is rendered beside the concentration figure it moves, because a
 * control this far from its effect reads as a preference rather than a filter.
 */
export function PrivateHoldingsConcentrationSwitch(): ReactElement {
  const { settings, isPending, save } = usePrivateHoldings()

  return (
    <>
      <style>{SWITCH_CSS}</style>
      <Switch
        label="Count private holdings in concentration"
        hint="Off by default: with them in, the largest position is permanently the one nobody can trade and the figure says nothing."
        checked={settings.includeInConcentration}
        disabled={isPending}
        onChange={(includeInConcentration) => save({ ...settings, includeInConcentration })}
      />
    </>
  )
}

/**
 * The round price is what somebody paid on one day for one class of share. Age
 * and confidence travel with it everywhere so the figure is never read as a
 * quote.
 */
export function PrivateHoldingValuationNote({
  valuation,
  haircutBp,
}: {
  valuation: EffectiveValuation
  haircutBp: number
}): ReactElement {
  const afterHaircutMinor = removeBasisPoints(valuation.impliedValueMinor, haircutBp)

  return (
    <div className="nw-valuation">
      <span className="nw-marks">
        <StatusPill
          tone={CONFIDENCE_TONE[valuation.confidence]}
          label={`${valuation.confidence} confidence`}
        />
        <StatusPill tone="manual" label={`Priced ${describeAge(valuation.ageDays)}`} />
      </span>
      <p className="nw-lede" style={{ marginTop: 6 }}>
        {CONFIDENCE_NOTE[valuation.confidence]}
      </p>
      <dl>
        <dt>Holding</dt>
        <dd>
          {valuation.instrumentName} · {valuation.round ?? 'no round recorded'} of {valuation.date}
        </dd>
        <dt>Round price</dt>
        <dd>
          {formatMinor(valuation.pricePerShareMinor, valuation.currency, {
            maximumFractionDigits: 2,
          })}{' '}
          per share
        </dd>
        <dt>Value</dt>
        <dd className="nw-headline">
          {formatMinor(afterHaircutMinor, valuation.currency)}
          {haircutBp > 0 && (
            <>
              {' '}
              <small>
                after a {formatBasisPoints(haircutBp)} haircut on{' '}
                {formatMinor(valuation.impliedValueMinor, valuation.currency)}
              </small>
            </>
          )}
        </dd>
      </dl>
    </div>
  )
}

function Switch({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string
  hint: string
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}): ReactElement {
  return (
    <label className="nw-switch">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        {label}
        <small>{hint}</small>
      </span>
    </label>
  )
}

function Haircut({
  settings,
  disabled,
  onChange,
}: {
  settings: PrivateHoldingsSettings
  disabled: boolean
  onChange: (next: PrivateHoldingsSettings) => void
}): ReactElement {
  const [draft, setDraft] = useState<string | null>(null)
  const shown = draft ?? percentOf(settings.haircutBp)

  const commit = (event: FormEvent): void => {
    event.preventDefault()
    const haircutBp = basisPointsOf(shown)
    setDraft(null)
    if (haircutBp !== null && haircutBp !== settings.haircutBp) onChange({ ...settings, haircutBp })
  }

  return (
    <form onSubmit={commit}>
      <fieldset>
        <legend>Haircut</legend>
        <div className="nw-haircut">
          <input
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={shown}
            disabled={disabled}
            aria-label="Haircut in percent"
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
          />
          <span aria-hidden="true">%</span>
          <SegmentedControl
            label="Haircut preset"
            choices={HAIRCUT_PRESETS_BP.map((bp) => ({
              id: String(bp),
              label: formatBasisPoints(bp, 0),
            }))}
            value={String(settings.haircutBp)}
            onChange={(id) => {
              setDraft(null)
              onChange({ ...settings, haircutBp: Number(id) })
            }}
          />
        </div>
        <p className="nw-lede">
          Applied on top of the round price every time it is read. The valuation events stay exactly
          as they were entered, so changing your mind about the discount does not rewrite what the
          company was once worth.
        </p>
      </fieldset>
    </form>
  )
}

function percentOf(bp: number): string {
  return new Decimal(bp).dividedBy(BASIS_POINTS_FULL / 100).toFixed()
}

function basisPointsOf(percent: string): number | null {
  const trimmed = percent.trim()
  if (trimmed === '') return null
  try {
    const bp = new Decimal(trimmed).times(BASIS_POINTS_FULL / 100).toDecimalPlaces(0).toNumber()
    return bp >= 0 && bp <= BASIS_POINTS_FULL ? bp : null
  } catch {
    return null
  }
}
